/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */

import React, { useState, useEffect } from 'react';
import { Card, CardFooter, CardBody, Col, Row, Alert } from 'reactstrap';
import { withFormik, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Link, withRouter } from 'react-router-dom';
import { find } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import formatPostCode from '@cordata/buffet/build/helpers/formatPostCode';
import useDuplicate from '~/utils/duplicate/hook';
import { all, spread } from 'axios';

import {
  adminInstance as axios,
  referenceAdminInstance as refData,
  toastSuccess,
} from '~/utils';
import { Button, CardTitle, Field, FormAlerts, useFeatures } from '@cordata/buffet';

const serializeFacilityDistributionList = (fdl, f) => ({
  facilityUid: f,
  name: fdl.name,
  emailAddress: fdl.emailAddress,
});

const formEnhancer = withFormik({
  validationSchema: Yup.object().shape({
    facilityName: Yup.string().required(),
    pacsLink: Yup.string().url().nullable(),
    facilityDistributionLists: Yup.array().of(Yup.object().shape({
      emailAddress: Yup.string().email().required(),
    })),
  }),

  mapPropsToValues: (props) => {
    const address = props.facilityAddresses && props.facilityAddresses[0];
    const workPhone = find(props.facilityPhones, { attributes: { phoneType: 'Work' } });
    const fax = find(props.facilityPhones, { attributes: { phoneType: 'Fax' } });

    return {
      id: props.facility && props.facility.id,
      facilityName: props.facility && props.facility.attributes.facilityName,
      pacsLink: props.facility && props.facility.attributes.pacsLink,
      npiNumber: props.facility && props.facility.attributes.npiNumber,
      addr1: address && address.attributes.addr1,
      addr2: address && address.attributes.addr2,
      city: address && address.attributes.city,
      provinceIso: address && address.attributes.provinceIso,
      postCode: address ? address.attributes.postCode : '',
      phone: workPhone && workPhone.attributes.phoneNumber,
      fax: fax && fax.attributes.phoneNumber,
      isInSelections: props.facility ? props.facility.attributes.isInSelections : true,
      facilityDistributionLists: props.facilityDistributionLists &&
        props.facilityDistributionLists.map(fdl => ({
          id: fdl.id,
          name: fdl.attributes.name,
          emailAddress: fdl.attributes.emailAddress,
        })),
    };
  },

  handleSubmit: (values, { setErrors, setSubmitting, ...bag }) => {
    const { props: { history } } = bag;

    const p = {
      ...Object.keys(values)
        .filter(i =>
          values[i] !== null && values[i] !== '')
        .reduce((a, m) => ({
          ...a,
          [m]: values[m],
        }), {}),
      state: values.provinceIso && values.provinceIso.attributes.divisionAbbrevIso,
      mainPhoneNumber: values.phone && values.phone !== '' ? values.phone.replace(/\D/g, '') : null,
      mainFaxNumber: values.fax && values.fax !== '' ? values.fax.replace(/\D/g, '') : null,
      orgUid: localStorage.getItem('org'),
    };

    if (!values.id) {
      axios
        .post('/facility', p)
        .then((r) => {
          const fdlPromises = values.facilityDistributionLists ? values.facilityDistributionLists.map(fdl => (fdl.id ? axios.put(`/facility-distribution-list/${fdl.id}`, serializeFacilityDistributionList(fdl, r.data.data.id)) : axios.post('/facility-distribution-list', serializeFacilityDistributionList(fdl, r.data.data.id)))) : [];

          return all([
            r,
            ...fdlPromises,
          ]);
        })
        .then(spread((r => r)))
        .then(() => toastSuccess())
        .then(() => history.push('/admin/facilities'))
        .catch((e) => {
          setErrors({
            server: e.response.data,
            ...e.response.data.errors,
          });
        })
        .then(() => setSubmitting(false));
    } else {
      axios
        .put(`/facility/${values.id}`, p)
        .then(r => all([
          ...values.facilityDistributionLists.map(fdl => (fdl.id ? axios.put(`/facility-distribution-list/${fdl.id}`, serializeFacilityDistributionList(fdl, r.data.data.id)) : axios.post('/facility-distribution-list', serializeFacilityDistributionList(fdl, r.data.data.id)))),
        ]))
        .then(spread((r => r)))
        .then(() => toastSuccess())
        .then(() => history.push('/admin/facilities'))
        .catch((e) => {
          setErrors({
            server: e.response.data,
            ...e.response.data.errors,
          });
        })
        .then(() => setSubmitting(false));
    }
  },

  displayName: 'FacilitiesForm',
  validateOnChange: false,
  validateOnBlur: false,
});

const FacilitiesForm = ({
  values,
  errors,
  setFieldValue,
  handleSubmit,
  isSubmitting,
  submitCount,
}) => {
  const [availableStates, setAvailableStates] = useState([]);
  const { hasAppKickstarter } = useFeatures();
  const { handleDuplicateCheck } = useDuplicate({ api: 'admin/facility', labelKey: 'facilityName', pageUrl: 'admin/facilities' });

  useEffect(() => {
    refData
      .get('/reference-country-division?country-abbrev-iso=US')
      .then(r => r.data.data)
      .then((r) => {
        setAvailableStates(r);

        if (values.provinceIso) {
          setFieldValue('provinceIso', find(r, ['attributes.divisionAbbrevIso', values.provinceIso]));
        }
      });
  }, []);

  useEffect(() => {
    if (!values.id && values.facilityName) {
      handleDuplicateCheck(values);
    }
  }, [values.id, values.facilityName]);

  return (
    <Card>
      <form noValidate>
        <CardBody>
          { submitCount >= 1 && errors && !errors.orgUid && <FormAlerts errors={errors} /> }
          { submitCount >= 1 && errors && errors.orgUid && <Alert color="danger">We are unable to complete this action.<hr />Organization is required.</Alert> }

          <Field
            name="facilityName"
            required
          />

          <Row form>
            <Col sm>
              <Field
                name="pacsLink"
                label="PACS Link"
                type="url"
                placeholder="http://example.com"
              />
            </Col>

            <Col sm>
              <Field
                name="npiNumber"
                label="NPI Number"
                placeholder="Enter NPI number"
              />
            </Col>
          </Row>

          <Field
            name="addr1"
            label="Address Line 1"
            placeholder="Enter address line 1"
          />

          <Field
            name="addr2"
            label="Address Line 2"
            placeholder="Enter Address Line 2"
          />

          <Row form>
            <Col sm>
              <Field
                name="city"
              />
            </Col>

            <Col sm>
              <Field
                name="provinceIso"
                label="State"
                placeholder="Enter State"
                type="select"
                options={availableStates}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.attributes.divisionLabel}
              />
            </Col>

            <Col sm>
              <Field
                name="postCode"
                format={formatPostCode}
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="phone"
                type="tel"
                label="Main Phone Number"
              />
            </Col>

            <Col sm>
              <Field
                name="fax"
                type="tel"
                label="Main Fax Number"
              />
            </Col>
          </Row>

          <Field
            name="isInSelections"
            type="checkbox"
            label="Active"
            placeholder="Yes"
          />

          <CardTitle className="mt-5">Facility Distribution Lists</CardTitle>

          <FieldArray
            name="facilityDistributionLists"
            render={({ push, remove }) => (
              <>
                {values.facilityDistributionLists &&
                  values.facilityDistributionLists.map((e, i) => (
                    <React.Fragment key={`fdl-${i}`}>
                      <Button
                        size="sm"
                        outline
                        color="danger"
                        spear
                        type="button"
                        onClick={() => (e.id ? axios.delete(`/facility-distribution-list/${e.id}`).then(() => remove(i)) : remove(i))}
                      >
                        <FontAwesomeIcon icon="minus" className="mr-2" />
                        Remove Email Address
                      </Button>

                      <Row form>
                        <Col sm>
                          <Field
                            name={`facilityDistributionLists.${i}.name`}
                            label="Name"
                            placeholder="Enter Name"
                          />
                        </Col>

                        <Col sm>
                          <Field
                            name={`facilityDistributionLists.${i}.emailAddress`}
                            label="Email Address"
                            placeholder="Enter Email Address"
                            type="email"
                            required
                          />
                        </Col>
                      </Row>

                      {i !== values.facilityDistributionLists.length - 1 && <hr />}
                    </React.Fragment>
                  ))}

                <Button
                  size="sm"
                  outline
                  color="tertiary"
                  spear
                  type="button"
                  onClick={() => push({ name: '', emailAddress: '' })}
                >
                  <FontAwesomeIcon icon="plus" className="mr-2" />
                  Add Email Address
                </Button>
              </>
            )}
          />

          {hasAppKickstarter && (
            <Field
              name="id"
              placeholder=" "
              disabled
            />
          )}
        </CardBody>

        <CardFooter className="d-flex">
          <div className="ml-auto">
            <Button
              type="cancel"
              disabled={isSubmitting}
              tag={Link}
              to="/admin/facilities"
            >
              Cancel
            </Button>

            <Button type="button" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
              <FontAwesomeIcon icon="save" className="mr-2" />
              {isSubmitting ? 'Working ...' : 'Save'}
            </Button>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
};

export default withRouter(formEnhancer(FacilitiesForm));
